import { faCaretDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { RadioButtons } from '@/common/forms/RadioButtons';
import { WBSButton } from '@/common/WBSButton';
import { CheckboxLabel, WBSCheckbox } from '@/common/WBSCheckbox';
import {
  Divider,
  FormField,
  StyledInput,
  StyledTextarea,
} from '~/styles/forms/formElements';
import { SelectGroup } from '~/styles/forms/styledSelectGroup';
import { Heading2, Heading3 } from '~/styles/forms/text';

export const Route = createFileRoute('/study-helper-application')({
  component: StudyHelperApplicationPage,
  beforeLoad: () => {
    return {
      pageBackground: 'white',
      noContainer: true,
    };
  },
});

type AgreementResponse = 'agree' | 'disagree' | 'not_sure';

interface FormData {
  // Personal info
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  birthdate: string;
  gender: string;
  preferred_languages: string[];

  // Congregation info
  congregation_name: string;
  congregation_address_1: string;
  congregation_address_2: string;
  congregation_city: string;
  congregation_state: string;
  congregation_zip: string;
  congregation_country: string;
  congregation_website: string;

  // Radio responses
  accept_wbs_agreement: AgreementResponse | '';
  faithful_membership: AgreementResponse | '';
  teaching_consistency: AgreementResponse | '';
  use_followup_system: AgreementResponse | '';
  volunteer_acknowledgment: AgreementResponse | '';

  // Other
  comments_motivation: string;
  referral_source: string;
  has_existing_account: boolean | null;
  existing_account_email: string;
}

const initialFormData: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  birthdate: '',
  gender: '',
  preferred_languages: [],

  congregation_name: '',
  congregation_address_1: '',
  congregation_address_2: '',
  congregation_city: '',
  congregation_state: '',
  congregation_zip: '',
  congregation_country: '',
  congregation_website: '',

  accept_wbs_agreement: '',
  faithful_membership: '',
  teaching_consistency: '',
  use_followup_system: '',
  volunteer_acknowledgment: '',

  comments_motivation: '',
  referral_source: '',
  has_existing_account: null,
  existing_account_email: '',
};

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'MX', name: 'Mexico' },
  { code: 'BR', name: 'Brazil' },
  { code: 'PH', name: 'Philippines' },
  { code: 'IN', name: 'India' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'KE', name: 'Kenya' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'GH', name: 'Ghana' },
];

const LANGUAGES = [
  { code: 'EN', name: 'English' },
  { code: 'ES', name: 'Spanish' },
  { code: 'FR', name: 'French' },
  { code: 'PT_BR', name: 'Portuguese (Brazil)' },
];

// Translations Needed
const AGREEMENT_OPTIONS = [
  { value: 'agree', label: 'Agree' },
  { value: 'disagree', label: 'Disagree' },
  { value: 'not_sure', label: 'Not Sure' },
];

function StudyHelperApplicationPage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const YES_NO_OPTIONS = [
    { value: 'yes', label: t('wbs.yes') },
    { value: 'no', label: t('wbs.no') },
  ];

  const updateField = <K extends keyof FormData>(
    field: K,
    value: FormData[K],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleLanguage = (langCode: string) => {
    setFormData((prev) => ({
      ...prev,
      preferred_languages: prev.preferred_languages.includes(langCode)
        ? prev.preferred_languages.filter((l) => l !== langCode)
        : [...prev.preferred_languages, langCode],
    }));
  };

  const allRadiosAnswered =
    formData.accept_wbs_agreement !== '' &&
    formData.faithful_membership !== '' &&
    formData.teaching_consistency !== '' &&
    formData.use_followup_system !== '' &&
    formData.volunteer_acknowledgment !== '';

  const isFormValid =
    formData.first_name.trim() !== '' &&
    formData.last_name.trim() !== '' &&
    formData.email.trim() !== '' &&
    allRadiosAnswered;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('/api/v3/helper-applications/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: formData.phone || null,
          address_1: formData.address_1 || null,
          address_2: formData.address_2 || null,
          city: formData.city || null,
          state: formData.state || null,
          zip: formData.zip || null,
          country: formData.country || null,
          birthdate: formData.birthdate || null,
          gender: formData.gender || null,
          preferred_languages:
            formData.preferred_languages.length > 0
              ? formData.preferred_languages
              : null,
          congregation_name: formData.congregation_name || null,
          congregation_address_1: formData.congregation_address_1 || null,
          congregation_address_2: formData.congregation_address_2 || null,
          congregation_city: formData.congregation_city || null,
          congregation_state: formData.congregation_state || null,
          congregation_zip: formData.congregation_zip || null,
          congregation_country: formData.congregation_country || null,
          congregation_website: formData.congregation_website || null,
          radio_responses: {
            accept_wbs_agreement: formData.accept_wbs_agreement,
            faithful_membership: formData.faithful_membership,
            teaching_consistency: formData.teaching_consistency,
            use_followup_system: formData.use_followup_system,
            volunteer_acknowledgment: formData.volunteer_acknowledgment,
          },
          comments_motivation: formData.comments_motivation || null,
          referral_source: formData.referral_source || null,
          has_existing_account: formData.has_existing_account,
          existing_account_email: formData.existing_account_email || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit application');
      }

      setSubmitSuccess(true);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : 'An error occurred',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <PageWrapper>
        <Container>
          {/* Translation Needed */}
          <Title>Application Submitted</Title>
          <SuccessMessage>
            {/* Translation Needed */}
            Thank you for your application to become a Study Helper! We will
            review your application and get back to you soon.
          </SuccessMessage>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <Title>{t('lessons.study-helper-application')}</Title>
        <Form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <FormField>
            <Heading2>{t('wbs.personal-information')}</Heading2>
            <GridContainer>
              <div>
                <Heading3>
                  {t('wbs.first-name')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => updateField('first_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('wbs.last-name')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => updateField('last_name', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>
                  {t('notifications.email')} <Required>*</Required>
                </Heading3>
                <StyledInput
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  required
                />
              </div>
              <div>
                <Heading3>{t('wbs.phone-2')}</Heading3>
                <StyledInput
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                />
              </div>
              <div>
                <Heading3>{t('wbs.birthdate')}</Heading3>
                <StyledInput
                  type="date"
                  value={formData.birthdate}
                  onChange={(e) => updateField('birthdate', e.target.value)}
                />
              </div>
              <div>
                <Heading3>{t('wbs.gender')}</Heading3>
                <SelectGroup>
                  <select
                    value={formData.gender}
                    onChange={(e) => updateField('gender', e.target.value)}
                  >
                    {/* Translation Needed */}
                    <option value="">Select Gender</option>
                    <option value="male">{t('wbs.male')}</option>
                    <option value="female">{t('wbs.female')}</option>
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Address */}
          <FormField>
            <Heading2>{t('actions.address-2')}</Heading2>
            <GridContainer>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('actions.address-i-optional')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.address_1}
                  onChange={(e) => updateField('address_1', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('actions.address-ii-optional')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.address_2}
                  onChange={(e) => updateField('address_2', e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Heading3>{t('wbs.city')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                />
              </div>
              <div>
                <Heading3>{t('wbs.state-province')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                />
              </div>
              <div>
                <Heading3>{t('forum.zip-postal')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                />
              </div>
              <div>
                <Heading3>{t('wbs.country')}</Heading3>
                <SelectGroup>
                  <select
                    value={formData.country}
                    onChange={(e) => updateField('country', e.target.value)}
                  >
                    <option value="">{t('wbs.select-country')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Preferred Languages */}
          <FormField>
            <Heading2>{t('wbs.preferred-languages')}</Heading2>
            <CheckboxGroup>
              {LANGUAGES.map((lang) => (
                <CheckboxLabel key={lang.code}>
                  <WBSCheckbox
                    checked={formData.preferred_languages.includes(lang.code)}
                    onChange={() => toggleLanguage(lang.code)}
                  />
                  {lang.name}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </FormField>

          <Divider />

          {/* Congregation Information */}
          <FormField>
            <Heading2>{t('wbs.congregation-information')}</Heading2>
            <GridContainer>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('wbs.congregation-name')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_name}
                  onChange={(e) =>
                    updateField('congregation_name', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>{t('actions.congregation-address-i')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_address_1}
                  onChange={(e) =>
                    updateField('congregation_address_1', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Heading3>
                  {t('actions.congregation-address-ii-optional')}
                </Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_address_2}
                  onChange={(e) =>
                    updateField('congregation_address_2', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <Heading3>{t('wbs.city')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_city}
                  onChange={(e) =>
                    updateField('congregation_city', e.target.value)
                  }
                />
              </div>
              <div>
                <Heading3>{t('wbs.state-province')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_state}
                  onChange={(e) =>
                    updateField('congregation_state', e.target.value)
                  }
                />
              </div>
              <div>
                <Heading3>{t('forum.zip-postal')}</Heading3>
                <StyledInput
                  type="text"
                  value={formData.congregation_zip}
                  onChange={(e) =>
                    updateField('congregation_zip', e.target.value)
                  }
                />
              </div>
              <div>
                <Heading3>{t('wbs.country')}</Heading3>
                <SelectGroup>
                  <select
                    value={formData.congregation_country}
                    onChange={(e) =>
                      updateField('congregation_country', e.target.value)
                    }
                  >
                    <option value="">{t('wbs.select-country')}</option>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <span className="icon">
                    <FontAwesomeIcon icon={faCaretDown} />
                  </span>
                </SelectGroup>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                {/* Translation Needed */}
                <Heading3>Congregation Website</Heading3>
                <StyledInput
                  type="url"
                  value={formData.congregation_website}
                  onChange={(e) =>
                    updateField('congregation_website', e.target.value)
                  }
                  placeholder="https://"
                  style={{ width: '100%' }}
                />
              </div>
            </GridContainer>
          </FormField>

          <Divider />

          {/* Radio Questions */}
          <FormField>
            <Heading2>
              {/* Translation Needed */}
              Please indicate your agreement with the following statements{' '}
              <Required>*</Required>
            </Heading2>

            <RadioQuestion>
              {/* Translation Needed */}
              <QuestionText>
                I accept the World Bible School statement of faith and teaching
                guidelines.
              </QuestionText>
              <RadioButtons
                name="accept_wbs_agreement"
                options={AGREEMENT_OPTIONS}
                value={formData.accept_wbs_agreement}
                onChange={(value) =>
                  updateField(
                    'accept_wbs_agreement',
                    value as AgreementResponse,
                  )
                }
              />
            </RadioQuestion>

            <RadioQuestion>
              {/* Translation Needed */}
              <QuestionText>
                I am a faithful member of a local Church of Christ congregation.
              </QuestionText>
              <RadioButtons
                name="faithful_membership"
                options={AGREEMENT_OPTIONS}
                value={formData.faithful_membership}
                onChange={(value) =>
                  updateField('faithful_membership', value as AgreementResponse)
                }
              />
            </RadioQuestion>

            <RadioQuestion>
              {/* Translation Needed */}
              <QuestionText>
                I will teach only what is consistent with Scripture and WBS
                materials.
              </QuestionText>
              <RadioButtons
                name="teaching_consistency"
                options={AGREEMENT_OPTIONS}
                value={formData.teaching_consistency}
                onChange={(value) =>
                  updateField(
                    'teaching_consistency',
                    value as AgreementResponse,
                  )
                }
              />
            </RadioQuestion>

            <RadioQuestion>
              {/* Translation Needed */}
              <QuestionText>
                I will use the follow-up system to connect students with local
                congregations.
              </QuestionText>
              <RadioButtons
                name="use_followup_system"
                options={AGREEMENT_OPTIONS}
                value={formData.use_followup_system}
                onChange={(value) =>
                  updateField('use_followup_system', value as AgreementResponse)
                }
              />
            </RadioQuestion>

            <RadioQuestion>
              {/* Translation Needed */}
              <QuestionText>
                I understand this is a volunteer ministry and I will not receive
                compensation.
              </QuestionText>
              <RadioButtons
                name="volunteer_acknowledgment"
                options={AGREEMENT_OPTIONS}
                value={formData.volunteer_acknowledgment}
                onChange={(value) =>
                  updateField(
                    'volunteer_acknowledgment',
                    value as AgreementResponse,
                  )
                }
              />
            </RadioQuestion>
          </FormField>

          <Divider />

          {/* Additional Information */}
          <FormField>
            {/* Translation Needed */}
            <Heading2>Additional Information</Heading2>
            <div style={{ marginBottom: '1rem' }}>
              {/* Translation Needed */}
              <Heading3>
                Why do you want to become a Study Helper? (Optional)
              </Heading3>
              <StyledTextarea
                rows={4}
                value={formData.comments_motivation}
                onChange={(e) =>
                  updateField('comments_motivation', e.target.value)
                }
                // Translation Needed
                placeholder="Tell us about your motivation..."
              />
            </div>
            <div>
              {/* Translation Needed */}
              <Heading3>How did you hear about us? (Optional)</Heading3>
              <StyledInput
                type="text"
                value={formData.referral_source}
                onChange={(e) => updateField('referral_source', e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
          </FormField>

          <Divider />

          {/* Existing Account */}
          <FormField>
            {/* Translation Needed */}
            <Heading2>Existing Account</Heading2>
            {/* Translation Needed */}
            <QuestionText>
              Do you already have a World Bible School account?
            </QuestionText>
            <RadioButtons
              name="has_existing_account"
              options={YES_NO_OPTIONS}
              value={
                formData.has_existing_account === true
                  ? 'yes'
                  : formData.has_existing_account === false
                    ? 'no'
                    : ''
              }
              onChange={(value) =>
                updateField('has_existing_account', value === 'yes')
              }
            />
            {formData.has_existing_account && (
              <div>
                {/* Translation Needed */}
                <Heading3>Email associated with your existing account</Heading3>
                <StyledInput
                  type="email"
                  value={formData.existing_account_email}
                  onChange={(e) =>
                    updateField('existing_account_email', e.target.value)
                  }
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </FormField>

          {submitError && <ErrorMessage>{submitError}</ErrorMessage>}

          <WBSButton type="submit" disabled={!isFormValid || isSubmitting}>
            {/* Translations Needed */}
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </WBSButton>
        </Form>
      </Container>
    </PageWrapper>
  );
}

const PageWrapper = styled.div`
  background: var(--grey-100);
  min-height: 70vh;
  padding: 40px 20px;
`;

const Container = styled.div`
  background: var(--white);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin: 0 auto;
  max-width: 800px;
  padding: 45px 60px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  color: var(--grey-900);
  font-family: var(--raleway);
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const GridContainer = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;

  & > div {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const RadioQuestion = styled.div`
  margin-bottom: 1.5rem;
`;

const QuestionText = styled.p`
  color: var(--grey-800);
  font-family: var(--raleway);
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.75rem;
`;

const Required = styled.span`
  color: var(--wbs-red);
`;

const ErrorMessage = styled.div`
  background-color: #fee2e2;
  border: 1px solid #ef4444;
  border-radius: 6px;
  color: #dc2626;
  font-family: var(--raleway);
  font-size: 14px;
  margin-top: 1rem;
  padding: 12px 16px;
`;

const SuccessMessage = styled.div`
  color: var(--grey-700);
  font-family: var(--raleway);
  font-size: 16px;
  line-height: 1.6;
  text-align: center;
`;
